import React, {
  useEffect, useState, useCallback, useMemo, Dispatch, SetStateAction, useRef, lazy, Suspense
} from 'react';
import {
  Link,
  useHistory,
} from 'react-router-dom';
import {
  OpenAPI,
  Other,
} from 'simplydo/interfaces';
import {
  Divider, Message, Button, Label, Icon, Card, Popup, Menu, Loader, Dropdown, Input,
} from 'semantic-ui-react';
import {
  useTranslation,
} from 'react-i18next';
import { DateInput } from 'semantic-ui-calendar-react';
import { useDidMount } from 'utils/useDidMount';
import {
  useAppSelector, useAppDispatch,
} from 'store';
import {
  ChallengesReducer,
  defaultInitialState as challengeReducerDefaultState,
} from 'reducers/challenges';
import toast from 'react-hot-toast';
import actions from 'actions';
import util from 'utils/utils';
import api from 'api';
import useTheme from 'theme/useTheme';
import styled from 'styled-components';
import moment from 'moment';

import { confirmMerge } from 'components/lib/IdeaMerge';
import { EmptyBox } from 'components/lib/UI';
import { HandledIdeaPreview as IdeaPreview } from 'components/lib/Ideas';
import { ChallengeChooser, UserChooser } from 'components/lib/Choosers';
import ProjectBoard from 'components/lib/ProjectBoard';
import BoardEvents from 'components/lib/ProjectBoard/BoardEvents';
import ChallengeIdeaFilters from 'components/lib/Filters/ChallengeIdeaFilters';
import EmojiChooser from 'components/lib/Emoji/EmojiChooser';
import PinIdeaHelper from 'components/lib/Ideas/PinIdeaHelper';
import ProductTour from 'components/lib/ProductTour';
import IdeaCard from './IdeaCard';
import ChallengeMyIdeas from './MyIdeas';

import {
  updateIdea as updateIdeaAction,
  pinIdea as pinIdeaAction,
  labelIdea as labelIdeaAction,
  bulkUpdateAssessors,
  bulkRemoveAssessors as bulkRemoveAssessorsAction,
  bulkUpdateStatus,
  bulkMoveIdeas,
  voteForIdea as voteForIdeaAction,
  addTag as addTagAction,
  unTag as unTagAction,
  Pagination,
  IdeaDeletionModal,
} from './IdeaActions';

import {
  IdeaListContainer, StyledModal, ActionPopup,
} from './Styles';

const SpreadsheetIdeas = lazy(() => import('./SpreadsheetIdeas'));

const StyledIdeasContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const StyledIdeaItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 100%;
  flex: 1;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (!theme.sizes.isComputer ? 'column-reverse' : 'row')};
  align-items: ${({ theme }) => (!theme.sizes.isComputer ? 'flex-start' : 'center')};
  margin-bottom: ${({ theme }) => (theme.sizes.isMobile ? '0' : '10px')};
  justify-content: space-between;
`;

const StyledFilters = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? 'column' : 'row')};
  align-items: center;
  min-width: fit-content;
  .ui.button, a, .ui.dropdown {
    min-width: fit-content;
    ${({ theme }) => theme.sizes.isMobile && `
      margin-bottom: 5px;
      width: 100%;
    `}
  }
  .ui.button, .ui.dropdown {
    height: 33px;
  }
  ${({ theme }) => theme.sizes.isMobile && `
    width: 100%;
  `}
  ${({ theme }) => !theme.sizes.isLargeComputer && `
    margin-bottom: 5px;
  `}
`;

const ChallengeFilterKeys = [
  'ideaFilter',
  'tagFilter',
  'ideaIncludes',
  'ideaOrder',
  'ideaOrderDirection',
  'ideaPage',
  'ideaFieldChoices',
  'adminHasCommented',
  'projectBoardFilter',
];

const IdeaViewTypes = [
  {
    key: 'card',
    text: 'Card',
    value: 'card',
    icon: 'vcard',
  },
  {
    key: 'list',
    text: 'List',
    value: 'list',
    icon: 'list',
  },
  {
    key: 'board',
    text: 'Board',
    value: 'board',
    icon: 'trello',
  },
  {
    key: 'spreadsheet',
    text: 'Table',
    value: 'spreadsheet',
    icon: 'table',
    requiredFeature: 'ideaSpreadsheetView',
  },
];

type IdeasProps = {
  challenge: Other.IChallenge;
  myIdeas: Other.IIdeaList[];
  newIdea: () => void,
  allStamps: any[],
  updateAllStamps: Dispatch<SetStateAction<any[]>>,
  popularTags: Other.ITag[],
  updatePopularTags: Dispatch<SetStateAction<any[]>>,
  availableGroups: Other.IGroup[],
  setAvailableGroups: Dispatch<SetStateAction<any[]>>,
  availableOrganisations: Other.IOrganisation[],
  setAvailableOrganisations: Dispatch<SetStateAction<any[]>>,
  toggleStamp: (stamp: any) => void,
}

const Ideas = (props: IdeasProps) => {
  const {
    challenge,
    myIdeas,
    newIdea,
    allStamps,
    updateAllStamps,
    popularTags,
    updatePopularTags,
    availableGroups,
    setAvailableGroups,
    availableOrganisations,
    setAvailableOrganisations,
    toggleStamp,
  } = props;
  const [actionPopupOpen, setActionPopupOpen] = useState(false);

  const dispatch = useAppDispatch();

  const onFollow = useCallback((context: string, id: string) => {
    dispatch(actions.user.onFollow(context, id));
  }, [dispatch]);

  const updateFilters = useCallback((filters: Partial<ChallengesReducer>) => {
    dispatch(actions.challenges.updateIdeaFilters(filters));
  }, [dispatch]);

  const setIdeaViewType = useCallback((viewType: string, resetParams=true) => {
    if (resetParams) {
      // Remove view type from query params
      const params = new URLSearchParams(window.location.search);
      params.delete('queryView');
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    }
    if (['card', 'list'].includes(viewType) && util.localStorageIsSupported()) {
      localStorage.setItem('ideaViewTypePreference', viewType);
    }
    dispatch(actions.challenges.setIdeaViewType(viewType));
  }, [dispatch]);

  const updateTagList = useCallback((id: string, tagList: Other.ITag[]) => {
    dispatch(actions.challenges.update(id, { tagList }));
  }, [dispatch]);

  const updateDefaultLane = useCallback((updatedLane) => {
    dispatch(actions.challenges.receive({
      ...challenge,
      projectBoard: {
        ...(challenge.projectBoard ?? {}),
        defaultLane: updatedLane,
      },
    }));
  }, [challenge, dispatch]);

  const { t } = useTranslation();
  const history = useHistory();

  const user: OpenAPI.GET<'/users/me'> | undefined = useAppSelector((state) => state.user);
  const hasUser: boolean = !!user;
  const { organisation } = user ?? {};
  const ideaMergeEnabled = organisation?.enabledFeatures?.includes('ideaMerging');

  const challengesReducer: ChallengesReducer = useAppSelector((state) => state.challenges);
  const {
    tagFilter,
    ideaFilter: query,
    ideaIncludes: filter,
    ideaOrder: sort,
    ideaOrderDirection: sortDirection,
    ideaPage: page,
    stampsFilter: stamps,
    ideaLimit: limit,
    ideaFieldChoices: fieldChoices,
    projectBoardLanesFilter,
    postFetchPreviewIndex,
    groupFilter,
    orgFilter,
    projectBoardFilter,
    ideaViewType,
    adminHasCommented,
    filtersVisible,
  } = challengesReducer;

  const [ideaState, setIdeaState] = useState({
    ideas: [],
    nextPageAvailable: false,
    previousPageAvailable: false,
    total: 0,
    loading: false,
  });
  const [showMyIdeas, setShowMyIdeas] = useState(true);

  const setIdeasLoading = useCallback((isLoading) => setIdeaState((prevState) => ({ ...prevState, loading: isLoading })), [setIdeaState]);
  const [ideasVotedFor, setIdeasVotedFor] = useState(0);
  const [challengeChooserIdea, setChallengeChooserIdea] = useState('');
  const [selectedIdeas, setSelectedIdeas] = useState([]);
  const [assessmentAssignIdeas, setAssessmentAssignIdeas] = useState([]);
  const [projectAssignIdeas, setProjectAssignIdeas] = useState([]);
  const [emojiStampChooserOpen, setEmojiStampChooserOpen] = useState('');
  const [deletingIdeas, setDeletingIdeas] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [openToComments, setOpenToComments] = useState(false);
  const [previewId, setPreviewId] = useState('');
  const [availableSortParameters, setAvailableSortParameters] = useState({});
  const [availableFilterParameters, setAvailableFilterParameters] = useState({});
  const [allProjectAssignees, setAllProjectAssignees] = useState<Other.IUser[]>([]);
  const [removingIdeaFromMerge, setRemovingIdeaFromMerge] = useState<string>('');

  const [allChallengeProjectLanes, setAllChallengeProjectLanes] = useState([]);
  const [selectingMultipleCards, setSelectingMultipleCards] = useState(false);

  const challengeId = challenge?._id;
  const {
    ideas, total, loading, nextPageAvailable, previousPageAvailable,
  } = ideaState;
  const theme = useTheme();

  const params = new URLSearchParams(window.location.search);
  const paramIdeaId = params.get('idea');
  const paramViewType = params.get('queryView');
  useEffect(() => {
    if (paramViewType && IdeaViewTypes.map((v) => v.value).includes(paramViewType)) {
      setIdeaViewType(paramViewType, false);
    }
  }, [paramViewType, setIdeaViewType]);

  const canGoPrevIdea = useMemo(() => ((ideas.findIndex((i) => i._id === previewId)) > 0) || previousPageAvailable, [ideas, previousPageAvailable, previewId]);
  const canGoNextIdea = useMemo(() => ((ideas.findIndex((i) => i._id === previewId)) !== (ideas.length - 1)) || nextPageAvailable, [ideas, nextPageAvailable, previewId]);

  const listView = useMemo(() => (ideaViewType === 'list'), [ideaViewType]);
  const { ideaFilter } = useAppSelector((state) => state.challenges);
  const filterDebounce = useRef<ReturnType<typeof setTimeout>>();
  const updateSearchTerm = useCallback((term) => {
    updateFilters({ ideaFilter: term, ideaPage: 1 });
  }, [updateFilters]);

  useEffect(() => {
    setAssessmentAssignIdeas([]);
    setChallengeChooserIdea('');
    setEmojiStampChooserOpen('');
    setSelectedIdeas([]);
    setIdeaState((prevState) => ({ ...prevState, ideas: [] }));
    updateFilters({
      ideaIncludes: ideaViewType === 'board' ? 'submitted' : null,
      ideaLimit:
        // eslint-disable-next-line no-nested-ternary
        (ideaViewType === 'board' || ideaViewType === 'spreadsheet')
          ? 10000
          : (ideaViewType === 'list' ? 30 : 16),
    });
  }, [ideaViewType, setIdeasLoading, updateFilters]);

  useEffect(() => {
    if (!previewId) setOpenToComments(false);
  }, [previewId]);

  const openIdeaPreview = useCallback((ideaId) => {
    setPreviewId(ideaId);
  }, []);

  useDidMount(() => {
    setPreviewId(paramIdeaId);
  });

  useEffect(() => {
    if (ideaViewType !== 'board') {
      setSelectingMultipleCards(false);
    }
  }, [ideaViewType]);

  const movePreviewIdea = useCallback((amount) => {
    const ideaIndex = ideas.findIndex((i) => i._id === previewId);
    if (ideaIndex === -1) return;
    const newIndex = (ideaIndex + amount);
    if ((newIndex === -1) && previousPageAvailable) {
      // Set page to (current page - 1)
      // Set postFetchPreviewIndex to (limit - 1)
      updateFilters({ ideaPage: (page - 1), postFetchPreviewIndex: limit - 1 });
    } else if ((newIndex === ideas.length) && nextPageAvailable) {
      // Set page to (current page + 1)
      // Set postFetchPreviewIndex to (0)
      updateFilters({ ideaPage: (page + 1), postFetchPreviewIndex: 0 });
    } else {
      const newPreviewId = ideas.map((i) => i._id)[newIndex];
      setPreviewId(newPreviewId);
    }
  }, [ideas, previewId, nextPageAvailable, previousPageAvailable, page, limit, updateFilters]);

  const shouldFetchIdeas = useMemo(() => (challengeId && hasUser), [challengeId, hasUser]);
  const fetchIdeas = useCallback(() => {
    // Non-users can view this page, but we don't
    // want to load ideas. So don't enter load loop if no user
    if (!shouldFetchIdeas) return;
    const tags = tagFilter.join(',');
    setIdeasLoading(true);

    const queryParams = {
      page, query, tags, filter: filter || [], sort, sortDirection, stamps, groupFilter, orgFilter, fieldChoices, limit, adminHasCommented, projectBoardFilter, projectBoardLanesFilter
    };

    const queryFunc = (handleLoading=false) => {
      if (handleLoading) {
        setIdeasLoading(true);
      }
      api.challenges.getIdeas(challengeId, { ...queryParams, contentType: 'full' }, (data) => {
        setIdeaState((prevState) => ({
          ...prevState,
          ideas: data.ideas,
          nextPageAvailable: data.nextPageAvailable,
          previousPageAvailable: data.previousPageAvailable,
          total: data.total,
        }));
        setIdeasVotedFor(data.ideasVotedFor);
        updateAllStamps(data.allStamps);
        updatePopularTags(data.popularTags);
        setAvailableGroups(data.availableGroups);
        setAvailableOrganisations(data.availableOrganisations);
        setInvitations(data.invitations);
        setAvailableFilterParameters(data.availableFilterParameters);
        setAvailableSortParameters(data.availableSortParameters);
        if (postFetchPreviewIndex !== null) {
          setPreviewId(data.ideas.map((i) => i._id)[postFetchPreviewIndex]);
        }
        setIdeasLoading(false);
        setAllProjectAssignees(data.allProjectAssignees);
      }, (err) => {
        toast.error(err.message);
        setIdeasLoading(false);
      });
    }

    // Spreadsheet we always use full content type
    if (ideaViewType === 'spreadsheet') {
      queryFunc();
    } else {
      // Get the idea list with a limited set of information for quicker loading
      api.challenges.getIdeas(challengeId, { ...queryParams, contentType: 'basic' }, ({
        ideas: basicIdeas
      }) => {
        setIdeaState((prevState) => ({
          ...prevState, ideas: basicIdeas, loading: false,
        }));
        // Get the idea list with full information
        queryFunc();
      }, (err) => {
        toast.error(err.message);
        setIdeasLoading(false);
      });
    }
  }, [shouldFetchIdeas, tagFilter, setIdeasLoading, page, query, filter, sort, sortDirection, stamps, groupFilter, orgFilter, fieldChoices, limit, adminHasCommented, projectBoardFilter, projectBoardLanesFilter, ideaViewType, challengeId, updateAllStamps, updatePopularTags, setAvailableGroups, setAvailableOrganisations, postFetchPreviewIndex]);

  useEffect(() => {
    if (filterDebounce.current) {
      clearTimeout(filterDebounce.current);
    }
    filterDebounce.current = setTimeout(() => {
      filterDebounce.current = undefined;
      fetchIdeas();
    }, 350);
  }, [fetchIdeas]);

  const getIdeaAssignees = useCallback(() => {
    api.challenges.getIdeaAssignees(challengeId, ({ allProjectAssignees: updatedAllProjectAssignees }) => {
      setAllProjectAssignees(updatedAllProjectAssignees);
    }, () => {});
  }, [challengeId]);

  const updateTagListWithTag = useCallback((tag) => {
    if (challenge) {
      const tagList = [...challenge.tagList];
      const index = tagList.findIndex((allTag) => allTag._id === tag._id);
      tagList[index] = tag;

      updateTagList(challenge._id, tagList);
    }
  }, [updateTagList, challenge]);

  const onSelectIdea = useCallback((idea) => {
    const prevSelectedIdeas = Object.assign([], selectedIdeas);
    const index = prevSelectedIdeas.indexOf(idea._id);
    if (index === -1) prevSelectedIdeas.push(idea._id);
    else prevSelectedIdeas.splice(index, 1);
    setSelectedIdeas(prevSelectedIdeas);
  }, [selectedIdeas, setSelectedIdeas]);

  // Update idea in ideas array using given data
  const onUpdateIdea = useCallback((ideaId, data) => {
    updateIdeaAction(setIdeaState, ideaId, data);
  }, [setIdeaState]);


  /* Update idea project info */
  const getChallengeProjectLanes = useCallback(() => {
    api.boards.getProjectLanes(challengeId, (data) => {
      setAllChallengeProjectLanes(data.lanes);
    }, () => {});
  }, [challengeId]);

  useEffect(() => {
    getChallengeProjectLanes();
  }, [getChallengeProjectLanes]);

  const updateProjectLane = useCallback((ideaId, laneId) => {
    const prevIdea = ideas.find((i) => i._id === ideaId);
    const prevIdeaBoards = prevIdea?.projectManagement?.boards || [];
    const ideaIsInCurrentBoard = prevIdeaBoards.findIndex((b) => b.forId === challengeId) > -1;
    const newLane = allChallengeProjectLanes.find((l) => l._id === laneId) ?? { name: challenge?.projectBoard?.defaultLane?.name ?? 'Default' };
    onUpdateIdea(ideaId, {
      projectManagement: {
        ...(prevIdea?.projectManagement || {}),
        boards:
          ideaIsInCurrentBoard
            ? prevIdeaBoards.map((b) => {
              if (b.forId !== challengeId) return b;
              return {
                ...b,
                lane: laneId,
                laneObject: newLane,
              };
            })
            : [{
              lane: laneId,
              forId: challengeId,
              laneObject: newLane,
            }],
      },
    });
    api.boards.updateProjectIdea(challengeId, ideaId, laneId, 0, () => {}, (err) => {
      onUpdateIdea(ideaId, { projectManagement: prevIdea.projectManagement });
      toast.error(err.message);
    });
  }, [allChallengeProjectLanes, challenge?.projectBoard?.defaultLane?.name, challengeId, ideas, onUpdateIdea]);
  /*  */


  // Pass array of idea ids and user ids, and remove the users as assessors from these ideas
  const bulkRemoveAssessors = useCallback((ideaIds, userIds) => {
    bulkRemoveAssessorsAction(ideaIds, challengeId, userIds, setAssessmentAssignIdeas, ideas, setIdeaState);
  }, [setAssessmentAssignIdeas, challengeId, ideas, setIdeaState]);

  const onRemoveExternalInvitation = useCallback((ideaId, invitation) => {
    const { _id, invitee } = invitation;
    setInvitations((prevInvitations) => prevInvitations.filter((i) => i._id !== _id));
    setIdeaState((prevState) => ({
      ...prevState,
      ideas: prevState.ideas.map((idea) => {
        if (idea._id !== ideaId) return idea;
        return {
          ...idea,
          invitedExternalAssessors: idea.invitedExternalAssessors.filter((i) => i._id !== invitee),
        };
      }),
    }));
  }, []);

  const pinIdea = useCallback((ideaId, isPinned) => {
    pinIdeaAction(setIdeaState, ideas, ideaId, isPinned);
  }, [setIdeaState, ideas]);

  // Add emoji stamp to idea, with choice of whether to apply or remove
  const labelIdea = useCallback((ideaId, label, isApplied) => {
    labelIdeaAction(ideas, ideaId, label, isApplied, toggleStamp, setIdeaState);
  }, [setIdeaState, ideas, toggleStamp]);

  const voteForIdea = useCallback((ideaId, addVote) => {
    voteForIdeaAction(setIdeaState, ideaId, ideas, ideasVotedFor, addVote, onFollow);
  }, [setIdeaState, ideas, ideasVotedFor, onFollow]);

  const addTag = useCallback((idea, newTagData) => {
    addTagAction(challenge.organisation, setIdeaState, updateTagListWithTag, ideas, idea, newTagData);
  }, [challenge, setIdeaState, ideas, updateTagListWithTag]);

  const unTag = useCallback((idea, tag) => {
    unTagAction(challenge.organisation, setIdeaState, updateTagListWithTag, idea, tag);
  }, [challenge, setIdeaState, updateTagListWithTag]);

  const createChallengeFromIdea = useCallback((ideaId) => {
    api.challenges.createFromIdea({ idea: ideaId }, (data) => {
      history.push(`/challenges/${data.challenge._id}`);
      toast.success('Challenge created');
    }, (err) => toast.error(err.message));
  }, [history]);

  const removeIdeaFromMerge = useCallback((mergeIdeaId: string, ideaId: string, onComplete = () => {}, onError = () => {}) => {
    // If you're removing the 2nd to last idea from a merge, then we delete the merge container, as there'd be no point in having it
    const mergeIdea = ideas.find((i) => i._id === mergeIdeaId);
    const willDeleteMergeContainer = mergeIdea?.children?.length <= 2;
    util.confirm(
      'Are you sure you want to remove this idea from the merge?',
      `The idea will not be deleted and will remain in the challenge. You can re-add it to the merge at any time.${willDeleteMergeContainer ? ' The merge container will be deleted as it will only contain one idea.' : ''}`,
    ).then(() => {
      setRemovingIdeaFromMerge(ideaId);
      api.ideas.removeIdeaFromMerge(mergeIdeaId, ideaId, () => {
        if (willDeleteMergeContainer) {
          // If we delete the merge container, we get the ID of the idea that wasn't deleted, and change the preview ID to that
          const nonDeletedIdeaId = mergeIdea?.children?.find((c) => c !== ideaId);
          if (nonDeletedIdeaId) {
            setPreviewId(nonDeletedIdeaId);
          }
        }
        setRemovingIdeaFromMerge('');
        fetchIdeas();
        onComplete(mergeIdeaId, ideaId);
      }, (err) => {
        toast.error(err.message);
        setRemovingIdeaFromMerge('');
        onError(mergeIdeaId, ideaId);
      });
    })
  }, [fetchIdeas, ideas]);

  const updateIdeaPositionMulti = useCallback((targetLane: string) => {
    const moveData = {
      targetLane, ideaIds: selectedIdeas,
    }
    api.boards.updateProjectIdeasMulti(challengeId, moveData, ({
      updatedIdeas,
    }) => {
      setIdeaState((prevState) => ({
        ...prevState,
        ideas: prevState.ideas.map((idea) => {
          if (!selectedIdeas.includes(idea._id)) {
            return idea;
          }
          const updatedIdea = updatedIdeas.find((i) => i._id === idea._id);
          return {
            ...idea,
            projectManagement: updatedIdea.projectManagement,
          }
        }),
      }));
      toast.success(`Updated lane for ${util.pluralise(updatedIdeas.length, 'idea', 'ideas', true)}`);
      setSelectedIdeas([]);
    }, (err) => {
      toast.error(`Unable to move ideas. ${err.message}`);
    });
  }, [challengeId, selectedIdeas]);

  const updateIdeaProjectDeadlineMulti = useCallback((deadline: Date) => {
    const formattedDeadline = moment(deadline, 'YYYY-MM-DD').toDate();
    api.boards.updateProjectIdeasDeadline(challengeId, { deadline: formattedDeadline, ideaIds: selectedIdeas }, ({ updatedIdeas }) => {
      setIdeaState((prevState) => ({
        ...prevState,
        ideas: prevState.ideas.map((idea) => {
          if (!(updatedIdeas.map((i) => i._id)).includes(idea._id)) {
            return idea;
          }
          const updatedIdea = updatedIdeas.find((i) => i._id === idea._id);
          return {
            ...idea,
            projectManagement: updatedIdea.projectManagement,
          }
        }),
      }));
      toast.success(`Updated deadline for ${util.pluralise(updatedIdeas.length, 'idea', 'ideas', true)}`);
    }, (err) => {
      toast.error(`Unable to update deadline. ${err.message}`);
    });
  }, [challengeId, selectedIdeas]);

  const bulkProjectAssign = useCallback((userIds: string[]) => {
    api.boards.bulkAssignProjectIdeas(
      challengeId,
      {
        ideaIds: selectedIdeas,
        userIds,
      },
      ({ updatedIdeas, allAssignees }) => {
        setIdeaState((prevState) => ({
          ...prevState,
          ideas: prevState.ideas.map((idea) => {
            if (!selectedIdeas.includes(idea._id)) {
              return idea;
            }
            const updatedIdea = updatedIdeas.find((i) => i._id === idea._id);
            return {
              ...idea,
              projectManagement: updatedIdea.projectManagement,
            }
          }),
        }));
        setSelectedIdeas([]);
        setAllProjectAssignees(allAssignees);
        toast.success(`Assigned ${util.pluralise(userIds.length, 'user', 'users', true)} for ${util.pluralise(updatedIdeas.length, 'idea', 'ideas', true)}`);
      },
      (err) => {
        toast.error(err.message);
      },
    )
  }, [challengeId, selectedIdeas]);

  const canManage = util.canManageChallenge(user, challenge);
  const RenderIdeaActions = useCallback((ideasToAction) => {
    const isBulk = (ideasToAction.length > 1);

    let idea = typeof ideasToAction[0] === 'object' ? ideasToAction[0] : ideas.find((i) => i._id === ideasToAction[0]);
    if (isBulk) {
      idea = {};
    }
    const ideaId = idea?._id;

    // You can only merge ideas, where either:
    // - None of the ideas have been merged
    // - One of the ideas is a merge parent, and the rest of the selected ideas have not been merged/are not merge parents
    const selectedIdeasData = ideas.filter((i) => ideasToAction.indexOf(i._id) > -1);
    const canMerge = isBulk && (
      selectedIdeasData.filter((i) => (i.parent || i.children)).length === 0
      || (
        selectedIdeasData.filter((i) => i.children).length === 1
        && selectedIdeasData.filter((i) => !(i.parent || i.children)).length === (selectedIdeasData.length - 1)
      )
    );

    if (!challenge) return null;
    return (
      <ActionPopup>
        <Menu
          secondary
          vertical
        >
          {ideaViewType === 'board'
            ? (
              <>
                <Dropdown item pointing='left' text='Move all selected ideas to project lane'>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      content={challenge?.projectBoard?.defaultLane?.name ?? 'Default'}
                      onClick={() => updateIdeaPositionMulti('default')}
                    />
                    {allChallengeProjectLanes.map((lane) => (
                      <Dropdown.Item
                        key={lane._id}
                        content={lane.name}
                        onClick={() => updateIdeaPositionMulti(lane._id)}
                      />
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <Menu.Item
                  name='Project assign users to all selected ideas'
                  onClick={() => setProjectAssignIdeas(selectedIdeas)}
                />
                <Dropdown item pointing='left' text='Set deadline' onClick={(e) => e.stopPropagation()}>
                  <Dropdown.Menu onClick={(e) => e.stopPropagation()}>
                    <DateInput
                      clearable
                      inline
                      placeholder='Set project deadline for all selected ideas'
                      dateFormat='YYYY-MM-DD'
                      iconPosition='left'
                      size='tiny'
                      onChange={(e, { value }) => updateIdeaProjectDeadlineMulti(value)}
                      closeOnMouseLeave={false}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown.Menu>
                </Dropdown>
              </>
            ) : null
          }
          <Popup
            on='hover'
            position='right center'
            content={(
              <PinIdeaHelper challenge={challenge} />
            )}
            trigger={(
              <Menu.Item
                icon='pin'
                name={idea?.isPinned ? 'Un-pin' : 'Pin'}
                onClick={() => (!isBulk ? pinIdea(ideaId, !idea?.isPinned) : bulkUpdateStatus(challengeId, selectedIdeas, setIdeaState, 'isPinned', true))}
              />
            )}
          />
          {ideaMergeEnabled ? (
            <Popup
              on='hover'
              content={
                canMerge
                  ? 'Merge ideas together'
                  : (
                    selectedIdeas.length > 1
                      ? 'You can only merge ideas where none of the previous ideas have been merged'
                      : 'Select at least two ideas to merge'
                  )
              }
              trigger={(
                <Menu.Item
                  disabled={(selectedIdeas.length < 2) || !canMerge}
                  icon='sitemap'
                  name='Merge'
                  onClick={() => {
                    setActionPopupOpen(false);
                    // @ts-ignore
                    confirmMerge(ideas.filter((sIdea) => selectedIdeas.includes(sIdea._id)), allChallengeProjectLanes, challenge)
                      .then(() => {
                        setSelectedIdeas([]);
                        fetchIdeas();
                      })
                      .catch(() => {});
                  }}
                />
              )}
            />
          ) : null}
          {challenge?.assessmentEnabled
            && (
              <Menu.Item
                icon='user plus'
                name='Assign assessors'
                onClick={() => setAssessmentAssignIdeas(isBulk ? ideasToAction : [idea._id])}
              />
            )
          }
          {ideaViewType !== 'board'
            ? (
              <>
                <Menu.Item
                  icon='arrow right'
                  name='Move to another challenge'
                  onClick={() => setChallengeChooserIdea(isBulk ? ideasToAction : [ideaId])}
                />
                {!isBulk && idea?.challengeCreation
                  && (
                    <Menu.Item
                      icon='target'
                      name='Create challenge from idea'
                      onClick={() => createChallengeFromIdea(ideaId)}
                    />
                  )
                }
              </>
            ) : null
          }

          {(!isBulk && listView)
            && (
              <Menu.Item
                icon='smile'
                name='Apply a stamp'
                onClick={() => setEmojiStampChooserOpen(ideaId)}
              />
            )
          }
          {isBulk
            && (
              <Menu.Item
                icon='ban'
                name='Deselect all'
                onClick={() => setSelectedIdeas([])}
              />
            )
          }
          {util.isInRole(user, 'org.managePermissions', user.organisation._id)
            && (
              <Popup
                disabled={!(!isBulk && !!idea?.children)}
                content='Delete this merged idea. None of the child ideas will be deleted, and they will be returned to the idea list.'
                trigger={(
                  <Menu.Item
                    icon='trash'
                    name={
                      !isBulk && !!idea?.children
                        ? (
                          `Delete merged idea`
                        ) : (
                          `Delete idea${!isBulk ? '' : 's'}`
                        )
                    }
                    onClick={() => {
                      if (isBulk) {
                        setDeletingIdeas(ideas.filter((i) => ideasToAction.indexOf(i._id) > -1));
                      } else {
                        setDeletingIdeas([idea]);
                      }
                    }}
                  />
                )}
              />

            )
          }
        </Menu>
      </ActionPopup>
    );
  }, [ideas, challenge, ideaViewType, allChallengeProjectLanes, ideaMergeEnabled, selectedIdeas, listView, user, updateIdeaPositionMulti, updateIdeaProjectDeadlineMulti, pinIdea, challengeId, fetchIdeas, createChallengeFromIdea]);

  const userId = user?._id;
  const onClosePreview = useCallback((ideaId) => {
    const idea = ideas.find((i) => i._id === ideaId);
    if (canManage) {
      getIdeaAssignees();
    }
    if (idea && (canManage || (idea.assessors?.indexOf(userId) > -1))) {
      api.ideas.getAssessmentScore(ideaId, ({ assessment }) => {
        onUpdateIdea(ideaId, { assessment });
      }, (err) => toast.error(err.message));
    }
  }, [ideas, canManage, userId, getIdeaAssignees, onUpdateIdea]);

  const hasPinnedIdea = useMemo(() => ideas.some((i) => i.isPinned), [ideas]);

  const RenderIdeaCard = useCallback((idea) => (
    // @ts-ignore
    <IdeaCard
      fetchIdeas={fetchIdeas}
      key={idea._id}
      idea={idea}
      invitations={invitations}
      ideasVotedFor={ideasVotedFor}
      challenge={challenge}
      addTag={(tag) => addTag(idea, tag)}
      unTag={(tag) => unTag(idea, tag)}
      voteForIdea={voteForIdea}
      listView={listView}
      labelIdea={labelIdea}
      onSelectIdea={onSelectIdea}
      selectedIdeas={selectedIdeas}
      ideaActions={RenderIdeaActions}
      onUpdateIdea={onUpdateIdea}
      removeAssessors={bulkRemoveAssessors}
      pinIdea={pinIdea}
      setPreviewOpen={openIdeaPreview}
      setOpenToComments={setOpenToComments}
      onRemoveExternalInvitation={onRemoveExternalInvitation}
      hasPinnedIdea={hasPinnedIdea}
      allChallengeProjectLanes={allChallengeProjectLanes}
      updateProjectLane={updateProjectLane}
    />
  ), [fetchIdeas, invitations, ideasVotedFor, challenge, voteForIdea, listView, labelIdea, onSelectIdea, selectedIdeas, RenderIdeaActions, onUpdateIdea, bulkRemoveAssessors, pinIdea, openIdeaPreview, onRemoveExternalInvitation, hasPinnedIdea, allChallengeProjectLanes, updateProjectLane, unTag, addTag]);

  const usedVotes = (parseInt(challenge?.voteLimit, 10) === ideasVotedFor);
  const votesRemaining = (parseInt(challenge?.voteLimit, 10) - ideasVotedFor);
  const emojiIdea = ideas.find((i) => i._id === emojiStampChooserOpen);

  const isFullSize = !theme.sizes.isMobile;

  const userChooserEnabledFeatures = useMemo(() => ((!user || !challenge) ? { search: true, invite: false } : { search: util.canManageChallenge(user, challenge), invite: true }), [user, challenge]);

  const activeIdeaViewType = useMemo(() => IdeaViewTypes.find((viewType) => (viewType.key === ideaViewType)), [ideaViewType]);

  const searchComponent = useMemo(() => (
    <Input
      size="small"
      placeholder={
        canManage
          ? 'Filter ideas by name, author, or project assignee...'
          : 'Filter ideas by name or author...'
      }
      icon='search'
      value={ideaFilter}
      onChange={(e) => updateSearchTerm(e.target.value)}
      style={{ height: 33, minWidth: canManage ? 325 : 250 }}
      fluid={theme.sizes.isMobile}
    />
  ), [canManage, ideaFilter, theme.sizes.isMobile, updateSearchTerm]);

  const numberOfFiltersNotDefault = useMemo(() => ChallengeFilterKeys.filter((key) => !util.variablesAreEqual(challengesReducer[key], challengeReducerDefaultState[key])).length, [challengesReducer]);

  return (
    <StyledIdeasContainer>
      <ProductTour
        tour='projectBoardOnIdeasPage'
        isReady={!!challenge && ideaViewType === 'board' && window.location.pathname.indexOf('/ideas') > -1}
      />

      <ChallengeIdeaFilters
        challenge={challenge}
        popularTags={popularTags}
        allStamps={allStamps}
        availableGroups={availableGroups}
        availableOrganisations={availableOrganisations}
        availableSortParameters={availableSortParameters}
        availableFilterParameters={availableFilterParameters}
        allProjectAssignees={allProjectAssignees}
      />

      <IdeaDeletionModal
        ideasToDelete={deletingIdeas}
        setIdeasToDelete={setDeletingIdeas}
        setIdeaState={setIdeaState}
        setSelectedIdeas={setSelectedIdeas}
      />
      <IdeaPreview
        openToComments={openToComments}
        ideaId={previewId}
        assessmentEnabled={challenge?.assessmentEnabled}
        closeModal={() => {
          setPreviewId('');
          onClosePreview(previewId);
        }}
        onUpdateIdea={onUpdateIdea}
        moveIdea={movePreviewIdea}
        canGoNextIdea={canGoNextIdea}
        canGoPrevIdea={canGoPrevIdea}
        removeIdeaFromMerge={removeIdeaFromMerge}
        removingIdeaFromMerge={removingIdeaFromMerge}
        isProjectBoard={ideaViewType === 'board'}
        challenge={challenge}
      />
      <StyledModal
        open={!!emojiStampChooserOpen}
        onClose={() => setEmojiStampChooserOpen('')}
        basic
        closeIcon
      >
        <EmojiChooser
          onComplete={(em) => labelIdea(emojiIdea._id, em, true)}
          existing={emojiIdea?.stamps}
          onRemoveExisting={(em) => labelIdea(emojiIdea._id, em, false)}
          useNative={false}
        />
      </StyledModal>
      <UserChooser
        controlled
        isOpen={projectAssignIdeas.length > 0}
        onClose={() => setProjectAssignIdeas([])}
        onComplete={(users) => bulkProjectAssign(users.map((u) => u._id))}
        forType='projectAssignee'
        forId={challengeId}
        clearOnComplete
        enabledFeatures={{ search: true, invite: false }}
        instructions='Choose multiple users to project assign to selected ideas.'
        confirm='Assign these users'
        searchFunction={(term, cb) => api.boards.searchProjectAssignees(challengeId, term, ({ users }) => cb(users), () => {})}
      />
      <UserChooser
        single={undefined}
        controlled
        isOpen={assessmentAssignIdeas.length > 0}
        onClose={() => setAssessmentAssignIdeas([])}
        onCompleteItems={assessmentAssignIdeas}
        onComplete={(users) => {
          // @ts-ignore
          const emailUsers = users.filter((u) => u.isEmailInvitee);
          // @ts-ignore
          const existingUsers = users.filter((u) => !u.isEmailInvitee);
          if (emailUsers.length) {
            api.invitations.createBulk({
              invitees: emailUsers.map((u) => u._id), invitationType: 'email', forType: 'ideaAssessor', forId: challengeId, forIdeas: assessmentAssignIdeas,
            }, (({ invitations: newInvitations }) => {
              setInvitations((prevInvitations) => [...prevInvitations, ...newInvitations]);
              toast.success(`Invitation${emailUsers.length > 1 ? 's' : ''} sent`);
            }), () => {});
            setIdeaState((prevState) => ({
              ...prevState,
              ideas: prevState.ideas.map((idea) => {
                if (assessmentAssignIdeas.indexOf(idea._id) === -1) return idea;
                return ({
                  ...idea,
                  invitedExternalAssessors: [...(idea?.assessment?.invitedExternalAssessors || []), ...(emailUsers.filter((e) => (idea?.assessment?.invitedExternalAssessors || []).indexOf(e) === -1))],
                });
              }),
            }));
          }
          bulkUpdateAssessors(existingUsers, challengeId, assessmentAssignIdeas, ideas, setIdeaState);
        }}
        externalInvitesInAudience={challenge?.visibility?.organisations?.length > 0}
        forType='assessor'
        forId={challengeId}
        audienceWarningText={<span>The users below are currently outside of the challenge audience. Users not in the challenge audience will be unable to view the challenge and their assessments. To ensure assessors can access their assessments, add them to the <a href={`/challenges/${challengeId}/settings/audience`} target="_blank" rel="noreferrer">challenge audience</a> after making this assignment.</span>}
        clearOnComplete
        enabledFeatures={userChooserEnabledFeatures}
        instructions={`Choose users to assign as assessors for ${assessmentAssignIdeas.length > 1 ? 'the selected ideas' : 'the selected idea'}. Assessors will be able to find the ideas on their 'Assessments' tab within this challenge.`}
        confirm='Assign these assessors'
        message={
          challenge?.ideaTemplateData?.assessmentClosed
            ? (
              <Message warning>
                <Message.Header>Assessment closed</Message.Header>
                <Message.Content>
                  Assessment is now closed for this challenge. If you assign assessors, they will not be able to complete their assessment. You can change this <Link to={`/challenges/${challenge._id}/settings/format`}>here</Link>.
                </Message.Content>
              </Message>
            ) : null
        }
        searchFunction={(term, cb) => api.search.challengeOwner(challengeId, term, ({ users }) => cb(users), () => {})}
        trigger={undefined}
      />
      {challenge?._id && challengeChooserIdea?.length > 0
        ? (
          // @ts-ignore
          <ChallengeChooser
            controlled
            trigger={undefined}
            isOpen={challengeChooserIdea.length > 0}
            onClose={() => setChallengeChooserIdea('')}
            onComplete={(c) => bulkMoveIdeas(setIdeaState, setSelectedIdeas, challengeChooserIdea, c._id, () => setChallengeChooserIdea(''))}
            instructions={'We\'ll move the selected idea(s) to another challenge that you can manage.'}
            ignores={[challenge._id]}
            messageProps={{ warning: true, content: 'Moving ideas to another challenge will remove any existing assessments and any collaborators.' }}
          />
        ) : null
      }
      {(ideaViewType !== 'spreadsheet' && ideaViewType !== 'board')
        ? (
          <ChallengeMyIdeas
            myIdeas={myIdeas}
            style={{ marginBottom: 20 }}
            showMyIdeas={showMyIdeas}
            setShowMyIdeas={setShowMyIdeas}
          />
        ) : null
      }
      <FilterContainer>
        <StyledFilters>
          {ideaViewType !== 'board'
            ? (
              <>
                {(challenge?.voteLimit && (challenge?.votingVisibility === 'users' || util.canManageChallenge(user, challenge))) && <Label>{!usedVotes && <Icon name={'thumbs up'} />} {util.pluralise(votesRemaining, 'like', 'likes')} remaining</Label>}
              </>
            ) : null
          }

          <Button
            primary
            icon='filter'
            size='tiny'
            content={`${theme.sizes.isLargeComputer ? (filtersVisible ? 'Hide idea filters' : 'Show idea filters') : 'Filters'}${numberOfFiltersNotDefault ? ` (${numberOfFiltersNotDefault})` : ''}`}
            onClick={() => updateFilters({ filtersVisible: !filtersVisible })}
            style={{ marginRight: 5 }}
          />

          {theme.sizes.isMobile ? null : searchComponent}

          {util.canManageChallenge(user, challenge)
            ? (
              <React.Fragment>
                {ideaViewType === 'board'
                  ? (
                    <Button
                      content={selectingMultipleCards ? 'Exit selection mode' : 'Select multiple cards'}
                      icon='grab'
                      size='tiny'
                      onClick={() => setSelectingMultipleCards((prev) => !prev)}
                      style={{ width: 180, marginLeft: 5, marginRight: 0 }}
                    />
                  ) : (
                    <Button
                      size='tiny'
                      icon='check'
                      basic
                      content='Select all on page'
                      onClick={() => setSelectedIdeas((prevSelectedIdeas) => util.union(prevSelectedIdeas, ideas.map((i) => i._id)))}
                      style={{ marginLeft: 5, marginRight: 0 }}
                    />
                  )
                }
                {selectedIdeas && selectedIdeas.length > 0
                  && (
                    <Popup
                      on={theme.sizes.isComputer ? 'hover' : 'click'}
                      open={actionPopupOpen}
                      onOpen={() => setActionPopupOpen(true)}
                      onClose={() => setActionPopupOpen(false)}
                      hoverable
                      trigger={(
                        <Button primary icon='dropdown' size='tiny' content={util.pluralise(selectedIdeas.length, 'idea', 'ideas')} style={{ marginLeft: 5 }} />
                      )}
                      content={RenderIdeaActions(selectedIdeas)}
                      style={{ padding: 5 }}
                    />
                  )
                }
              </React.Fragment>
            ) : null
          }

          {ideaViewType === 'board'
            ? (
              <>
                {util.canManageChallenge(user, challenge)
                  ? (
                    <BoardEvents
                      forId={challengeId}
                      setCurrentIdeaId={setPreviewId}
                      buttonStyle={{ height: 32.5, marginLeft: theme.sizes.isMobile ? 0 : 5 }}
                      buttonProps={{ size: 'tiny' }}
                    />
                  ) : null
                }
                {filter === 'submitted' && theme.sizes.isComputer
                  ? (
                    <span
                      style={{
                        opacity: 0.7,
                        display: 'block',
                        margin: '0 10px',
                        maxWidth: 400,
                        fontSize: 12,
                        lineHeight: '16px',
                      }}
                    >
                      Draft ideas are hidden by default in board view. {theme.sizes.isLargeComputer ? 'You can change this in the filters.' : ''}
                    </span>
                  ) : null
                }

              </>
            ) : null
          }
        </StyledFilters>

        <StyledFilters>
          {util.canManageChallenge(user, challenge) && theme.sizes.isLargeComputer
            && <Link style={{ display: 'block' }} to={`/challenges/${challenge?._id}/settings/ideas`}><Icon name='cogs' /> {t('generic.settings')}</Link>
          }
          <Popup
            on='hover'
            content='Change idea view'
            trigger={(
              <Dropdown
                icon={null}
                direction='left'
                trigger={(
                  <Button
                    icon={activeIdeaViewType?.icon}
                    content={activeIdeaViewType?.text}
                    size='tiny'
                    style={{ marginLeft: theme.sizes.isMobile ? 0 : 10 }}
                    color='twitter'
                    className='project-board-ideas-page-1'
                  />
                )}
                options={[
                  !theme.sizes.isLargeComputer
                    ? {
                      key: 'settings',
                      value: 'settings',
                      as: Link,
                      to: `/challenges/${challenge?._id}/settings/ideas`,
                      icon: 'cogs',
                      text: 'View idea settings',
                    } : null,
                  ...IdeaViewTypes.filter(
                    (viewType) => (
                      viewType.key === 'board'
                        ? (
                          (challenge?.projectManagementVisibility === 'users')
                          || util.canManageChallenge(user, challenge)
                        ) : (
                          (!viewType.requiredFeature)
                          // @ts-ignore
                          || user?.organisation?.enabledFeatures?.includes(viewType.requiredFeature)
                        )
                    ),
                  ).map((viewType) => ({
                    ...viewType,
                    text: `${viewType.text} view`,
                  }))
                ].filter((item) => Boolean(item))} 
                onChange={(e, { value }) => setIdeaViewType(value as string)}
                selectOnBlur={false}
              />
            )}
          />
        </StyledFilters>
      </FilterContainer>

      {theme.sizes.isMobile ? <div style={{ marginBottom: 5, width: '100%' }}>
        {searchComponent}
      </div> : null}

      {!user && <>
        <Message>
          <h3>Please login to view this page</h3>
          <p>Register or login to your Simply Do account to track your own ideas or to view, comment, and like other ideas in this challenge.</p>
          <Divider section />
          <Button as={Link} to={`/login?then=/challenges/${challenge?._id}/ideas`} primary>Login or register</Button>
        </Message>
      </>}
      {user && (challenge?.ideaVisibility === 'users' || util.canManageChallenge(user, challenge))
        ? (
          <StyledIdeaItemsContainer>
            {listView
              ? (
                <>
                  {loading && <Loader active inline='centered' />}
                  <IdeaListContainer>
                    <colgroup>
                      {/* @ts-ignore */}
                      {isFullSize && canManage ? <col width="40" height="55" /> : null}
                      {/* @ts-ignore */}
                      {(canManage || hasPinnedIdea) ? <col width="35" height="55" /> : <col width="0" height="55" />}
                      <col width="48" />
                      {/* @ts-ignore */}
                      {isFullSize ? <col height="55" /> : null}
                      <col />
                      <col />
                    </colgroup>
                    <tbody>
                      {ideas.map((idea) => RenderIdeaCard(idea))}
                    </tbody>
                  </IdeaListContainer>
                </>
              ) : null
            }
            {ideaViewType === 'card'
              ? (
                <Card.Group itemsPerRow={4} doubling stackable style={{ marginBottom: 20, zIndex: 5 }}>
                  {loading && <Loader active centered />}
                  {ideas.map((idea) => RenderIdeaCard(idea))}
                </Card.Group>
              ) : null
            }
            {ideaViewType === 'board'
              ? (
                <ProjectBoard
                  isChallengeBoard
                  forId={challengeId}
                  getBoard={(idea) => idea?.projectManagement?.boards?.filter((b) => (b.forId === challengeId))[0]}
                  canProjectManage={canManage || challenge.projectManagementVisibility === 'users'}
                  canManage={canManage}
                  loading={loading}
                  assessmentEnabled={challenge?.assessmentEnabled}
                  defaultLane={challenge?.projectBoard?.defaultLane}
                  updateDefaultLane={updateDefaultLane}
                  ideas={ideas}
                  setIdeas={(updatedIdeas) => setIdeaState((prevState) => ({ ...prevState, ideas: updatedIdeas }))}
                  totalIdeas={total}
                  currentIdeaId={previewId}
                  setCurrentIdeaId={setPreviewId}
                  selectionMode={selectingMultipleCards ? 'multi' : undefined}
                  selectedIdeaIds={selectedIdeas}
                  setSelectedIdeaIds={setSelectedIdeas}
                />
              ) : null
            }
            {ideaViewType === 'spreadsheet'
              ? (
                <Suspense fallback={<div>
                  <Loader active inline='centered' />
                </div>}>
                  <SpreadsheetIdeas
                    challenge={challenge}
                    ideas={ideas}
                    loadingIdeas={loading}
                    setIdeas={(newIdeas) => {
                      setIdeaState((prevState) => ({ ...prevState, ideas: newIdeas }));
                    }}
                    fetchIdeas={fetchIdeas}
                    removeAssessors={bulkRemoveAssessors}
                    onRemoveExternalInvitation={onRemoveExternalInvitation}
                  />
                </Suspense>
              ) : null}
            {(!loading && ideas.length === 0 && ['board', 'spreadsheet'].indexOf(ideaViewType) === -1)
              ? (
                <EmptyBox>
                  <h2>{t('challenge.ideas.noneTitle')}</h2>
                  {challenge?.stage === 'published' && <Button primary icon='plus' content={t('challenge.ideas.new')} onClick={() => newIdea()} />}
                </EmptyBox>
              ) : null
            }
            {(!loading && ideas.length > 0 && ['board', 'spreadsheet'].indexOf(ideaViewType) === -1)
              ? (
                <Pagination
                  limit={limit}
                  limitValues={listView ? ['15', '30', '50', '100'] : ['16', '32', '52', '100']}
                  page={page}
                  total={total}
                  updateFilters={updateFilters}
                />
              ) : null
            }
          </StyledIdeaItemsContainer>
        ) : null
      }
    </StyledIdeasContainer>
  );
};

export default Ideas;
